import { Checkbox, Typography } from "@mui/material";
import { api } from "services/api.service";
import useSWRMutation from "swr/mutation";
import { useSWRConfig } from "swr";
import { BulkOrder } from "types/bulkOrder";
import { useMemo } from "react";
import { useAlert } from "./useAlert";
import { ApiError } from "types/apiError";
import { apiErrorList } from "utils/helpers";
import { userHasPermission } from "utils/userPermissions";
import useCurrentUser from "./useCurrentUser";

export const wasPacked = (bulkOrder: BulkOrder): boolean => {
  return ["packed", "completed"].includes(bulkOrder.state);
};

export const PostPackBulkOrderCheckbox = ({ bulkOrder }: { bulkOrder: BulkOrder }) => {
  const { mutate } = useSWRConfig();
  const { alertSuccess, alertError } = useAlert();

  const { trigger, isMutating } = useSWRMutation<number>(
    `/bulk_orders/${bulkOrder.manifestId}`,
    async (_key: string, _arg: { arg: { post_pack: boolean } }) => {
      const response = await api.put<BulkOrder>(`/bulk_orders/${bulkOrder.manifestId}`, { post_pack: true });

      return response.status;
    },
    {
      throwOnError: false,
      onSuccess: () => {
        // Revalidate all bulk_orders queries to refresh the table
        mutate((key) => typeof key === "string" && key.startsWith("bulk_orders"));
        alertSuccess("Bulk order marked as packed successfully");
      },
      onError: (error: ApiError) => {
        alertError(
          apiErrorList(error)
            ?.map((err) => `* ${err}`)
            .join("\n") ?? "Failed to mark bulk order as packed.",
        );
      },
    },
  );

  const packed = useMemo(() => wasPacked(bulkOrder), [bulkOrder]);
  const { currentUserPermissions } = useCurrentUser();
  const hasPermission = useMemo(
    () => userHasPermission("bulk_order_update", currentUserPermissions),
    [currentUserPermissions],
  );

  return (
    <>
      <Checkbox
        checked={packed}
        onChange={(_e) => {
          trigger();
        }}
        disabled={isMutating || packed || !hasPermission}
        sx={{ paddingLeft: 0 }}
      />
      <Typography sx={{ fontSize: 13 }}>{isMutating ? "Packing..." : packed ? "Packed" : "Mark as packed"}</Typography>
    </>
  );
};
